import { useEffect, useRef } from 'react'

interface UseAutoFillPagesArgs {
  /** How many items are on screen right now. */
  itemCount: number
  /** Keep pulling pages until the list is at least this long. */
  threshold: number
  hasMore: boolean
  isFetching: boolean
  loadNextPage: () => void
  /** Changes whenever the query changes, so a new list may fill itself again. */
  resetKey: string
}

/**
 * An infinite-scroll list opens on a page that may be too short to scroll, so
 * this pulls the next one until there is enough to grab.
 *
 * It refuses to ask twice for the same list length. If a page comes back and
 * the list has not grown, whatever is upstream is not appending, and asking
 * again produces exactly the same answer — a loop against the API that only
 * ends when the pages run out. That is what a two-letter search did in
 * production: it walked all nine pages of results and left the screen empty.
 *
 * The list not growing is a bug wherever it comes from, but it should cost one
 * wasted request, not one per page.
 */
export const useAutoFillPages = ({
  itemCount,
  threshold,
  hasMore,
  isFetching,
  loadNextPage,
  resetKey,
}: UseAutoFillPagesArgs) => {
  const requestedAtCount = useRef<number | null>(null)

  useEffect(() => {
    requestedAtCount.current = null
  }, [resetKey])

  useEffect(() => {
    if (isFetching || !hasMore) return
    if (itemCount === 0 || itemCount >= threshold) return
    if (requestedAtCount.current === itemCount) return

    requestedAtCount.current = itemCount
    loadNextPage()
  }, [itemCount, threshold, hasMore, isFetching, loadNextPage, resetKey])
}
